/**
 * ResponsiveTabNav — the canonical list-page status switcher.
 *
 * Renders the underline `TabNav` above a list/table, responsively:
 *   - Desktop (md+): full-width underline tab bar.
 *   - Mobile (<md): same underline tab bar, horizontally scrollable when the
 *     tabs overflow (`min-w-full w-max`).
 *
 * This replaces the old split where pages rendered a desktop-only `TabNav` and
 * pushed the mobile filter into chips *inside* `MobileExpandableTable`. The
 * switcher now lives above the table on every breakpoint, identical to the
 * publisher/admin Titles tabs — one shared piece instead of per-page wiring.
 *
 * Entrance animation is baked in via `AnimatedSection` (default delay 0.08) so
 * callers drop a single line.
 *
 * @layer-1 — shared, used by every list page that switches by status tab.
 */
import { AnimatedSection } from "./AnimatedSection";
import { TabNav, type TabDef } from "./DataTable";
import { cn } from "@/shared/lib/utils";

interface ResponsiveTabNavProps {
  /** Tab definitions (key + label). */
  tabs: TabDef[];
  /** Currently active tab key. */
  active: string;
  /** Fired with the selected tab key. */
  onChange: (key: string) => void;
  /**
   * Extra classes for the desktop wrapper only — used for horizontal page
   * padding (e.g. "lg:px-9"). Mobile stays full-bleed for edge-to-edge scroll.
   */
  className?: string;
  /** Entrance-animation delay (matches the surrounding section cadence). */
  delay?: number;
}

/**
 * Renders the desktop + mobile variants of the status tab bar above a table.
 */
export function ResponsiveTabNav({
  tabs,
  active,
  onChange,
  className,
  delay = 0.08,
}: ResponsiveTabNavProps) {
  return (
    <>
      {/* Desktop (md+): full-width underline */}
      <AnimatedSection delay={delay} className={cn("hidden md:block mb-5", className)}>
        <TabNav tabs={tabs} active={active} onChange={onChange} />
      </AnimatedSection>

      {/* Mobile (<md): same underline, scrolls horizontally on overflow */}
      <AnimatedSection delay={delay} className="md:hidden mb-4 overflow-x-auto">
        <TabNav tabs={tabs} active={active} onChange={onChange} className="min-w-full w-max" />
      </AnimatedSection>
    </>
  );
}
